/**
 * Chrome's Local Network Access (LNA), enforced since Chrome 142 (Oct 2025), blocks fetches
 * from a page in the "public" address space to a target that resolves to a "local" or
 * "loopback" address space unless the request explicitly opts in via the `targetAddressSpace`
 * fetch option — and it rejects the request outright if the declared space does not match the
 * target's actual space (e.g. declaring "local" for a request that resolves to loopback).
 *
 * Our BFF/API hosts (`qqt-api.*.gerdaugln.tech`) resolve to a private/RFC1918 IP depending on
 * VPN/DNS routing in deployed environments, so those need "local". Locally, the backend runs on
 * `localhost`/`127.0.0.1`, which Chrome classifies as "loopback" instead — declaring "local" for
 * it fails with "Request had a target IP address space of `local` yet the resource is in
 * address space `loopback`". So the address space must be derived from the target URL, not
 * hardcoded.
 *
 * Valid values, as of Chrome 138+, are "loopback" | "local" | "unknown" (public targets are
 * left unset).
 *
 * NOTE: `local`/`loopback` requests are also gated behind a browser permission prompt
 * (the `local-network-access` permission). Setting this option lets supporting Chrome versions
 * classify the request correctly, but the user (or an automated context like Playwright/CI)
 * may still need to grant that permission for the request to succeed.
 *
 * `targetAddressSpace` is a newer, non-standard fetch option not yet present in TS's lib.dom
 * types, hence the cast. See https://developer.chrome.com/blog/local-network-access.
 */

type TargetAddressSpace = "loopback" | "local";

const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1", "[::1]"]);

// RFC 1918 private ranges (10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16) and RFC 3927 link-local
// (169.254.0.0/16), which is where deployed BFF/API hosts can land depending on VPN/DNS routing.
const PRIVATE_IPV4_PATTERN =
  /^(10\.|192\.168\.|169\.254\.|172\.(1[6-9]|2\d|3[01])\.)\d{1,3}\.\d{1,3}$/;

/**
 * Derives the LNA `targetAddressSpace` for a fetch target, or `undefined` when the target is a
 * public hostname (the fetch option must be omitted, not set to any string, in that case).
 */
export const resolveTargetAddressSpace = (
  target: RequestInfo | URL,
): TargetAddressSpace | undefined => {
  const hostname = extractHostname(target);
  if (!hostname) {
    return undefined;
  }
  if (LOOPBACK_HOSTNAMES.has(hostname)) {
    return "loopback";
  }
  if (PRIVATE_IPV4_PATTERN.test(hostname) || hostname.endsWith(".local")) {
    return "local";
  }
  // Non-public private-use TLDs used internally (e.g. gerdaugln.tech resolving over VPN) are
  // treated as "local" — Chrome only accepts loopback/local/unknown for non-public targets, and
  // "unknown" would still fail LNA's actual-vs-declared check when the real target is private.
  if (hostname.endsWith(".gerdaugln.tech")) {
    return "local";
  }
  return undefined;
};

const extractHostname = (target: RequestInfo | URL): string | null => {
  try {
    if (typeof target === "string") {
      return new URL(target, window.location.origin).hostname;
    }
    if (target instanceof URL) {
      return target.hostname;
    }
    // Request instance
    return new URL(target.url).hostname;
  } catch {
    return null;
  }
};

export const withLocalNetworkAccess = (
  target: RequestInfo | URL,
  init?: RequestInit,
): RequestInit & { targetAddressSpace?: TargetAddressSpace } => {
  const targetAddressSpace = resolveTargetAddressSpace(target);
  if (!targetAddressSpace) {
    return { ...init };
  }
  return { ...init, targetAddressSpace };
};
